import { useEffect } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { Route, Routes } from 'react-router-dom'
import { AdminShell, AgentShell } from './components/layout/Shells'
import { RequireAuth, RequireRole } from './components/shared/Guards'
import { UNAUTHORIZED_EVENT } from './backend/httpAPI'
import { restoreSession, sessionExpired } from './store/authActionCreators'
import RootRedirect from './pages/Home'
import Login from './pages/auth/Login'
import { SecuritySetup, Verify } from './pages/auth/Mfa'
import { ForgotPassword, ResetPassword, Setup } from './pages/auth/Setup'
import AgentDashboard from './pages/agent/AgentDashboard'
import ApplicationWizard from './pages/agent/ApplicationWizard'
import { ApplicationDetail, ApplicationsList } from './pages/shared/Applications'
import { CustomerDetail, CustomerForm, CustomersList } from './pages/shared/Customers'
import { DueList, LoanDetail, LoansList, PaymentsQueue } from './pages/shared/Loans'
import AdminDashboard from './pages/admin/AdminDashboard'
import Agents from './pages/admin/Agents'
import AuditLog from './pages/admin/AuditLog'
import Products from './pages/admin/Products'
import Reports from './pages/admin/Reports'
import Settings from './pages/admin/Settings'
import { PrintAgreement, PrintReceipt, PrintStatement } from './pages/print/Print'
import './App.css'

/**
 * Route table. A page that exists but is not routed is invisible: add every new page here.
 * Guards are UX only; the API enforces session, role, permission and scope on every call.
 */
function App() {
  const dispatch = useDispatch()
  const store = useStore()

  useEffect(() => {
    dispatch(restoreSession())
    // Only a signed-in user can be "signed out": a 401 while anonymous (e.g. the first session
    // check) must not show the inactivity notice.
    const onUnauthorized = () => {
      const { status } = store.getState().auth
      if (status === 'ready' || status === 'mfa') dispatch(sessionExpired())
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [dispatch, store])

  return (
    <div className="app">
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/verify" element={<Verify />} />
        <Route path="/security/setup" element={<SecuritySetup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/setup" element={<Setup />} />

        <Route element={<RequireAuth />}>
          <Route path="/print/receipt/:id" element={<PrintReceipt />} />
          <Route path="/print/statement/:loanId" element={<PrintStatement />} />
          <Route path="/print/agreement/:applicationId" element={<PrintAgreement />} />

          <Route element={<RequireRole roles={['agent']} />}>
            <Route element={<AgentShell />}>
              <Route path="/agent" element={<AgentDashboard />} />
              <Route path="/agent/customers" element={<CustomersList base="/agent" />} />
              <Route path="/agent/customers/new" element={<CustomerForm base="/agent" />} />
              <Route path="/agent/customers/:id" element={<CustomerDetail base="/agent" />} />
              <Route path="/agent/applications" element={<ApplicationsList base="/agent" />} />
              <Route path="/agent/applications/new" element={<ApplicationWizard />} />
              <Route path="/agent/applications/:id" element={<ApplicationDetail base="/agent" />} />
              <Route path="/agent/repayments" element={<DueList base="/agent" />} />
              <Route path="/agent/loans/:id" element={<LoanDetail base="/agent" />} />
            </Route>
          </Route>

          <Route element={<RequireRole roles={['admin', 'super_admin']} />}>
            <Route element={<AdminShell />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/applications" element={<ApplicationsList base="/admin" />} />
              <Route path="/admin/applications/:id" element={<ApplicationDetail base="/admin" />} />
              <Route path="/admin/customers" element={<CustomersList base="/admin" />} />
              <Route path="/admin/customers/new" element={<CustomerForm base="/admin" />} />
              <Route path="/admin/customers/:id" element={<CustomerDetail base="/admin" />} />
              <Route path="/admin/loans" element={<LoansList base="/admin" />} />
              <Route path="/admin/loans/:id" element={<LoanDetail base="/admin" />} />
              <Route path="/admin/payments" element={<PaymentsQueue />} />
              <Route path="/admin/overdue" element={<DueList base="/admin" overdueOnly />} />
              <Route path="/admin/reports" element={<Reports />} />
              <Route path="/admin/products" element={<Products />} />
              <Route path="/admin/agents" element={<Agents />} />
              <Route path="/admin/audit" element={<AuditLog />} />
              <Route path="/admin/settings" element={<Settings />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </div>
  )
}

export default App
